import { ReactNode, useEffect, Suspense, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SkipLinks } from "@/components/ui/SkipLinks";
import { useOptimizedQueryKey } from "@/hooks/useOptimizedQueryKey";
import { prefetchOrgDashboardData } from "@/hooks/useOrgRoutePrefetch";
import { DashboardSidebarLayout } from "@/components/layout/DashboardSidebarLayout";
import { OrgSidebar } from "@/components/layout/OrgSidebar";
import { OrgDebugBanner } from "@/components/debug/OrgDebugBanner";
import { useAuthReady } from "@/hooks/useAuthReady";
import { PageLoader } from "@/components/ui/PageLoader";
import { supabase } from "@/integrations/supabase/client";
import type { OrganizationData } from '@/types/common';

interface OrgLayoutProps {
  children: ReactNode;
}

export function OrgLayout({ children }: OrgLayoutProps) {
  const queryClient = useQueryClient();
  const { organizationId, userId } = useOptimizedQueryKey();
  const { isReady, hasAccessToken } = useAuthReady();
  const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);

  // One-time auto-repair for missing org membership (fixes: "Access denied: organization membership required")
  const attemptedMembershipRepairRef = useRef(false);

  // Debug logging for org auth state (DEV only)
  useEffect(() => {
    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_ORG_FLOW === 'true') {
      console.debug('[OrgLayout] Auth state:', {
        isReady,
        hasAccessToken,
        organizationId,
        userId
      });
    }
  }, [isReady, hasAccessToken, organizationId, userId]);

  // CRITICAL: If user has org role but membership is missing, repair it once.
  useEffect(() => {
    if (!isReady || !hasAccessToken || !organizationId) return;
    if (attemptedMembershipRepairRef.current) return;

    attemptedMembershipRepairRef.current = true;

    (async () => {
      try {
        const { error } = await supabase.rpc('ensure_membership_for_current_user');
        if (error) {
          // Don’t spam; just log (DEV). Queries will keep failing and UI can show access card.
          if (import.meta.env.DEV) {
            console.warn('[OrgLayout] ensure_membership_for_current_user failed:', error);
          }
          return;
        }

        // Refresh any org-scoped queries now that membership exists.
        queryClient.invalidateQueries({
          predicate: (query) => {
            const k = query.queryKey as unknown[];
            return Array.isArray(k) && k.includes(organizationId);
          }
        });
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn('[OrgLayout] membership repair exception:', e);
        }
      }
    })();
  }, [isReady, hasAccessToken, organizationId, queryClient]);

  // CRITICAL: Wait for auth hydration before prefetching
  useEffect(() => {
    if (!isReady || !hasAccessToken || !organizationId) return;

    void prefetchOrgDashboardData(queryClient, organizationId, userId);
  }, [isReady, hasAccessToken, organizationId, queryClient, userId]);

  // Fetch organization data for header display
  useEffect(() => {
    let mounted = true;

    const fetchOrganization = async () => {
      if (!isReady || !hasAccessToken) return;

      try {
        if (!organizationId) return;

        const { data: orgData, error } = await supabase
          .from('organizations')
          .select('id, name, email, created_at')
          .eq('id', organizationId)
          .maybeSingle();

        if (error) {
          console.error('[OrgLayout] Failed to fetch organization:', error);
          return;
        }

        if (mounted && orgData) {
          setOrganizationData(orgData);
        }
      } catch (error) {
        console.error('[OrgLayout] Error fetching organization:', error);
      }
    };

    fetchOrganization();

    return () => {
      mounted = false;
    };
  }, [isReady, hasAccessToken, organizationId]);

  const DEBUG_ORG_FLOW = import.meta.env.VITE_DEBUG_ORG_FLOW === 'true';

  // Environment validation (when debug enabled)
  useEffect(() => {
    if (DEBUG_ORG_FLOW && typeof window !== 'undefined') {
      const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL;
      if (!publicAppUrl) {
        console.error('[OrgLayout] VITE_PUBLIC_APP_URL is not set!');
      } else if (window.location.origin !== publicAppUrl) {
        console.warn('[OrgLayout] Origin mismatch:', {
          origin: window.location.origin,
          config: publicAppUrl,
          impact: 'Edge functions may reject CORS requests'
        });
      } else {
        console.info('[OrgLayout] ✓ Origin validation passed');
      }
    }
  }, [DEBUG_ORG_FLOW]);

  // CRITICAL: Don't render children until auth is ready
  if (!isReady) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen bg-background">
      <SkipLinks />
      <DashboardSidebarLayout 
        sidebar={<OrgSidebar />}
        headerContext="organization"
        organizationData={organizationData}
      >
        {DEBUG_ORG_FLOW && <OrgDebugBanner />}
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </DashboardSidebarLayout>
    </div>
  );
}
